/**
 * All TA-board action handlers (optimistic update + API call + toast),
 * extracted from TAActionBoardView so the view itself stays small. Behavior is
 * unchanged — state still lives in the view; this hook only closes over it.
 */
import { useCallback, type Dispatch, type SetStateAction } from 'react';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taActionBoardApi';
import type {
  AskDestination,
  BoardResponse,
  FeedbackItem,
  FinalRoundsSection as FinalRoundsData,
  GradedSlot,
  PanelConfig,
  RoleBoard,
  SchedulingCandidate,
  StageRef,
  TrackingEntry,
  TrackingKind,
  TrackingMap,
} from '../../types/taActionBoard';
import type { ModalHandlers } from './ActionModals';
import type { CaseConfirmOpts } from './actionModalTypes';
import type { ApprovedEmail, OutboundEmail } from './FeedbackModalBodies';
import type { OfferChannel, OfferedEmail } from './OfferedModalBody';
import { archiveResultToast } from './BoardChrome';
import { moveEmailAction } from './moveEmailAction';
import { offerEmailAction } from './offerEmailAction';
import { caseBookingAction } from './caseBookingAction';
import { nudgeAction } from './nudgeAction';
import { TRACKING_KIND_STATUS } from './format';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { reportPanelSaved, reportStageChange } from './boardAnalytics';
import type { PendingAction } from './usePendingActions';
import type { SlotHoldsState } from './useSlotHolds';
import { holdHandlers } from './holdHandlers';

interface BoardActionDeps {
  myEmail: string;
  boardEmail: string;
  tracking: TrackingMap;
  setTracking: Dispatch<SetStateAction<TrackingMap>>;
  setBoard: Dispatch<SetStateAction<BoardResponse | null>>;
  markHandled: (opportunityId: string, label: string) => void;
  unmarkHandled: (opportunityId: string) => void;
  /** Queue an irreversible action behind the shared undo countdown. */
  deferAction: (action: Omit<PendingAction, 'secondsLeft'>) => void;
  /**
   * The board's shared Gmail token (null when not connected). Only the archive
   * action uses it: the approved rejection email then becomes a draft in the
   * caller's own mailbox instead of being handed to Make.
   */
  gmailToken: string | null;
  /** The board's calendar holds — every hold action updates this in place. */
  slotHolds: SlotHoldsState;
}

export function useBoardActions({
  myEmail,
  boardEmail,
  tracking,
  setTracking,
  setBoard,
  markHandled,
  unmarkHandled,
  deferAction,
  gmailToken,
  slotHolds,
}: BoardActionDeps) {
  const runOptimistic = useCallback(
    (apply: () => void, revert: () => void, call: () => Promise<unknown>, successMsg: string) => {
      apply();
      call()
        .then(() => showToast({ status: 'success', description: successMsg }))
        .catch((e) => {
          revert();
          showToast({
            status: 'error',
            description: e instanceof Error ? e.message : 'Action failed — nothing was changed.',
          });
        });
    },
    []
  );

  /** Local-only tracking update — for entries the SERVER already wrote (fr-booked). */
  const applyLocalTracking = useCallback(
    (opportunityId: string, entry: TrackingEntry, status: string) => {
      setTracking((prev) => ({
        ...prev,
        [opportunityId]: {
          entries: [...(prev[opportunityId]?.entries || []), entry],
          status,
        },
      }));
    },
    [setTracking]
  );

  /** Optimistic tracking update + POST (revert + toast on failure). */
  function applyTracking(
    opportunityId: string,
    kind: TrackingKind,
    slots: string[],
    note: string | null = null,
    /** Per-proposed-slot grade, stored at propose-time so the badge stays accurate. */
    slotMeta: { label: string; grade: string; start?: string }[] | null = null
  ) {
    const prev = tracking;
    const entry = {
      kind,
      slot: slots[0] || null,
      slots: slots.length > 0 ? slots : null,
      slotMeta: slotMeta && slotMeta.length > 0 ? slotMeta : null,
      note,
      at: new Date().toISOString(),
      by: myEmail,
    };
    const status = TRACKING_KIND_STATUS[kind];
    setTracking({
      ...prev,
      [opportunityId]: {
        entries: [...(prev[opportunityId]?.entries || []), entry],
        status,
      },
    });
    api
      .postTracking(opportunityId, { kind, slots, note, slotMeta: entry.slotMeta })
      .then(() => showToast({ status: 'success', description: `Tracking updated: ${status}.` }))
      .catch(() => {
        setTracking(prev);
        showToast({ status: 'error', description: 'Could not save the tracking entry.' });
      });
  }

  /** Replace one role's 🏁 finalRounds section (after template edits re-solve chains). */
  const updateFinalRounds = useCallback(
    (postingId: string, section: FinalRoundsData) => {
      setBoard((prev) =>
        prev
          ? {
              ...prev,
              roles: prev.roles.map((r) => (r.postingId === postingId ? { ...r, finalRounds: section } : r)),
            }
          : prev
      );
    },
    [setBoard]
  );

  const handlers: ModalHandlers = {
    // Merged flow: archive in Lever, save the approved rejection email as a Lever
    // note, and hand it to Make for sending — ONE request. The server archives
    // first; a note failure afterwards comes back as noteFailed (partial success).
    // `email` is the recruiter-approved subject+body, or null to archive silently.
    onArchive: (
      _role: RoleBoard,
      item: FeedbackItem,
      reason: string,
      email: ApprovedEmail | null,
      scheduleAt: string | null = null
    ) => {
      // Optimistic: the card greys out now, and un-greys if the countdown is
      // cancelled or the server rejects the archive.
      markHandled(item.opportunityId, 'Archived');

      const send = () => {
        api
          .postAction(
            {
              type: 'archive',
              opportunityId: item.opportunityId,
              boardEmail,
              reason,
              email: email ?? undefined,
              // Present ⇒ archive now, queue the email for this time (the send-later worker delivers it).
              scheduleAt: scheduleAt ?? undefined,
            },
            gmailToken
          )
          .then((r) => {
            // Emitted HERE, not when the modal closes: the action sits behind an undo countdown,
            // so a recruiter who cancels never archives anyone and counting the intent would
            // inflate rejections by every change of mind.
            reportStageChange({
              fromStage: item.stageName,
              toStage: 'Archived',
              isRejection: true,
              reason,
              withEmail: Boolean(email),
            });
            showToast(archiveResultToast(r, item.name, reason));
          })
          .catch((e) => {
            unmarkHandled(item.opportunityId);
            showToast({
              status: 'error',
              description: e instanceof Error ? e.message : 'Archive failed — nothing was changed.',
            });
          });
      };

      deferAction({
        id: `archive:${item.opportunityId}`,
        label: scheduleAt
          ? `Archiving ${item.name} and scheduling the email`
          : `Archiving ${item.name} and sending the email`,
        // See PendingAction.affects — an optimistic marker must not outlive the tab.
        affects: [item.opportunityId],
        run: send,
        undo: () => {
          unmarkHandled(item.opportunityId);
          showToast({
            status: 'info',
            description: `Stopped — ${item.name} was not archived and no email was ${scheduleAt ? 'scheduled' : 'sent'}.`,
          });
        },
      });
    },
    // targetStage is null only when the board payload carried no stage list
    // (e.g. an old cached board) — the proxy then falls back to the next stage.
    onMove: (
      _role: RoleBoard,
      item: FeedbackItem,
      targetStage: StageRef | null,
      email: OutboundEmail | null
    ) => {
      const stageName = targetStage?.name || item.nextStageName;
      const move = () =>
        api
          .postAction({
            type: 'move',
            opportunityId: item.opportunityId,
            boardEmail,
            targetStageId: targetStage?.id,
          })
          // AFTER the move lands, not before. Emitted up front, a rejected or failed move counted
          // as a stage change — the funnel would show progress that never happened.
          // AFTER the move lands, not before: emitted up front, a rejected move counted as a
          // stage change and the funnel showed progress that never happened.
          .then((result) => {
            reportStageChange({
              fromStage: item.stageName,
              toStage: stageName,
              isRejection: false,
              withEmail: Boolean(email),
            });
            return result;
          });

      // Move only — unchanged behaviour, nothing irreversible leaves the building.
      if (!email) {
        runOptimistic(
          () => markHandled(item.opportunityId, `Moved to ${stageName}`),
          () => unmarkHandled(item.opportunityId),
          move,
          `${item.name} moved to ${stageName}.`
        );
        return;
      }

      // Move AND send. The email is what cannot be taken back, so the whole pair
      // waits out the countdown: stopping it leaves Lever untouched too, which
      // is the behaviour a recruiter expects from one confirm.
      markHandled(item.opportunityId, `Moved to ${stageName}`);
      deferAction(
        moveEmailAction({ item, role: _role, stageName, email, gmailToken, move, unmarkHandled })
      );
    },
    // Role-level: ONE set of HOLD events for the recruiter's SELECTED slots
    // (checkboxes in the slot list; server caps at 10).
    ...holdHandlers({ slotHolds, tracking, setTracking, myEmail }),
    onOffered: (
      candidate: SchedulingCandidate,
      slotLabels: string[],
      channel: OfferChannel,
      role: RoleBoard,
      email: OfferedEmail | null,
      starts: string[],
      slotMeta: { label: string; grade: string; start?: string }[] | null = null
    ) => {
      applyTracking(
        candidate.opportunityId,
        'offered',
        slotLabels,
        channel === 'email'
          ? email
            ? 'Slot proposal emailed from the board'
            : 'Could not reach by phone — slot proposal drafted as email'
          : 'Reached by phone',
        slotMeta
      );
      // Same countdown as every other irreversible action. The channel is part
      // of the condition, not just the note above it: a phone proposal must
      // reach the candidate by phone and nothing else, and this is the last
      // gate before their inbox (Theresa, Sep 2026).
      if (channel === 'email' && email) {
        deferAction(offerEmailAction({ candidate, role, email, gmailToken }));
      }
      // Stamp WHICH held slots went to this candidate, so the overview can say
      // so and a later decline can find them. Best-effort: the offer itself is
      // already recorded, and an unstamped hold still shows as blocked.
      if (starts.length > 0) {
        api
          .recordOfferedSlots({
            postingId: role.postingId,
            opportunityId: candidate.opportunityId,
            candidateName: candidate.name,
            starts,
          })
          .then((r) => slotHolds.apply(role.postingId, r.holds))
          .catch(() => undefined);
      }
    },
    /**
     * Confirming a case now BOOKS it: calendar event with the panel, Lever
     * interview with the feedback form, and the candidate's confirmation email
     * with an .ics. Behind the same 10s countdown as every other irreversible
     * board action, because it reaches a real candidate's inbox.
     *
     * Without a slot there is nothing to book — the candidate was never offered
     * a blocked slot — so it degrades to the old tracking-only behaviour rather
     * than guessing a time.
     */
    onConfirmed: (candidate: SchedulingCandidate, role?: RoleBoard, start?: string | null, opts?: CaseConfirmOpts) => {
      applyTracking(candidate.opportunityId, 'confirmed', start ? [start] : []);
      markHandled(candidate.opportunityId, 'Interview confirmed');
      if (!role || !start) return;
      deferAction(caseBookingAction({ candidate, role, start, ...opts }));
    },
    // eslint-disable-next-line @typescript-eslint/max-params -- mirrors ModalHandlers.onAsk
    // `language` is the one the recruiter previewed in. Without it the server
    // re-derives from the role's tags, so an unedited DM would go out in the
    // OTHER language from the channel post the recruiter just approved.
    onAsk: (role: RoleBoard, destination: AskDestination, slots: GradedSlot[], message: string, messages: Record<string, string> = {}, language?: 'de' | 'en') => {
      const oppIds = role.scheduling.candidates.map((c) => c.opportunityId);
      api
        .askPanelists({
          postingId: role.postingId,
          opportunityIds: oppIds,
          // exactly what the recruiter ticked — the server re-matches each one
          // against its own grading and drops anything that is already free
          slots,
          candidateCount: role.scheduling.candidates.length,
          roleTitle: role.roleTitle,
          destination,
          // What the recruiter actually read and approved
          message,
          // DM mode: one text per recipient, each carrying only their own items
          messages,
          ...(language ? { language } : {}),
        })
        .then(async (r) => {
          showToast({
            status: r.ok ? 'success' : 'warning',
            description: r.ok
              ? destination.type === 'channel'
                ? `Panelists asked in ${destination.channel}.`
                : `Panelists asked via Slack DM (${r.sent.length} sent).`
              : `Some Slack DMs failed (${r.failed.join(', ')}).`,
          });
          setTracking(await api.fetchTracking(oppIds).catch(() => tracking));
        })
        // surface the proxy's message (e.g. "channel not found or the bot is
        // not a member") instead of a generic failure toast
        .catch((e) =>
          showToast({
            status: 'error',
            description:
              e instanceof Error && e.message ? e.message : 'Could not message the panelists.',
          })
        );
    },
    // The interview happened but the scorecard is missing — DM whoever ran it.
    onNudgeFeedback: nudgeAction,
    onSavePanel: (role: RoleBoard, panel: PanelConfig) => {
      api
        .savePanelConfig(role.postingId, panel)
        .then(() => api.fetchSlots(role.postingId, role.scheduling.candidates.length))
        .then(({ panel: saved, slots, error: slotError }) => {
          setBoard((prev) => applyScheduling(prev, role.postingId, saved, slots, slotError));
          reportPanelSaved(saved?.interviewers?.length ?? 0, slots?.length ?? 0);
          showToast({ status: 'success', description: 'Panel updated — slots re-graded.' });
        })
        .catch(() => {
          void logAnalyticsEvent('ta_board_panel_save_failed');
          showToast({ status: 'error', description: 'Could not save the panel.' });
        });
    },
  };

  /**
   * Re-grade ONLY one role's slots (the 🔄 button on the panel card) — one
   * calendar read per panelist instead of the full Lever board pull, so it is
   * the cheap way to see fresh availability after changing panelists.
   */
  const recheckSlots = useCallback(
    (role: RoleBoard): Promise<void> =>
      api
        .fetchSlots(role.postingId, role.scheduling.candidates.length)
        .then(({ panel, slots, error: slotError, diagnosis, lookaheadDays }) => {
          const days = lookaheadDays ?? role.scheduling.lookaheadDays ?? 10;
          setBoard((prev) =>
            applyScheduling(prev, role.postingId, panel, slots, slotError, diagnosis, lookaheadDays)
          );
          // ALWAYS report the outcome. Silence was indistinguishable from a dead
          // button: an unchanged calendar re-renders the identical list, so the
          // only way to tell "checked, nothing moved" from "nothing happened" is
          // to say so — including when the answer is zero.
          if (slotError) {
            showToast({ status: 'warning', description: `Calendars could not be read: ${slotError}` });
            return;
          }
          // Count BOOKABLE times, not rows: a grouped free window is one row but
          // several possible starts, and "3 slots" for a free week reads wrong.
          const workable = slots
            .filter((s) => s.grade !== 'fallback')
            .reduce((n, s) => n + Math.max(1, s.startOptions?.length ?? 1), 0);
          const shown = slots.filter((s) => s.suggested).length || Math.min(workable, 5);
          showToast({
            status: workable > 0 ? 'success' : 'warning',
            description:
              workable > 0
                ? `Calendars re-checked — ${workable} workable slot${workable === 1 ? '' : 's'} in the next ${days} days, ${shown} suggested.`
                : `Calendars re-checked — still no workable slot in the next ${days} days.${
                    diagnosis ? ` ${diagnosis}` : ''
                  }`,
          });
        })
        .catch((e) => {
          showToast({
            status: 'error',
            description:
              e instanceof Error && e.message
                ? `Could not re-check this panel’s calendars: ${e.message}`
                : 'Could not re-check this panel’s calendars.',
          });
        }),
    [setBoard]
  );

  return { handlers, applyTracking, applyLocalTracking, updateFinalRounds, recheckSlots };
}

/**
 * Merge a fresh panel + slot grading into one role. The candidates'
 * `suggestedPanel` (the "Case (suggested)" trail row and its "new to cand."
 * badges) is refreshed too — the server only sets it at board-build time, so
 * leaving it would show the OLD panel next to the new one until a full refresh.
 */
function applyScheduling(
  prev: BoardResponse | null,
  postingId: string,
  panel: PanelConfig | null,
  slots: RoleBoard['scheduling']['slots'],
  slotError: string | null,
  slotDiagnosis?: string | null,
  lookaheadDays?: number
): BoardResponse | null {
  if (!prev) return prev;
  return {
    ...prev,
    roles: prev.roles.map((r) =>
      r.postingId === postingId
        ? {
            ...r,
            scheduling: {
              ...r.scheduling,
              panel,
              slots,
              slotError,
              slotDiagnosis: slotDiagnosis ?? null,
              ...(lookaheadDays ? { lookaheadDays } : {}),
              candidates: r.scheduling.candidates.map((c) => ({
                ...c,
                suggestedPanel: panel?.interviewers ?? c.suggestedPanel,
                // The fixed-seat line explains the OLD seating; a re-grade can
                // change who sits, so drop it rather than leave it contradicting
                // the names beside it. The next board build recomputes it.
                panelFixed: panel ? [] : c.panelFixed,
                panelShort: panel
                  ? (panel.interviewers?.length ?? 0) <
                    (panel.interviewerCount ?? panel.interviewers?.length ?? 0)
                  : c.panelShort,
              })),
            },
          }
        : r
    ),
  };
}
